<template>
  <div class="app-layout">
    <the-navbar />
    <div class="app-layout__content">
      <div
        class="app-layout__sidebar-wrapper"
        :class="{ minimized: isSidebarMinimized }"
      >
        <div v-if="isFullScreenSidebar" class="d-flex justify--end">
          <va-button
            class="px-4 py-4"
            icon="close"
            flat
            color="dark"
            @click="onCloseSidebarButtonClick"
          />
        </div>
        <the-sidebar
          :width="sidebarWidth"
          :minimized="isSidebarMinimized"
          :minimizedWidth="sidebarMinimizedWidth"
        />
      </div>
      <div class="app-layout__page">
        <div class="layout fluid gutter--xl">
          <router-view />
        </div>
      </div>
    </div>
    <sticky-breadcrumb />

    <!-- Global Search Modal -->
    <global-search-modal />

    <!-- Global Actions Manager -->
    <global-actions-manager />
  </div>
</template>

<script>
import { useStore } from "vuex";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { onBeforeRouteUpdate } from "vue-router";
import TheSidebar from "@/components/sidebar/TheSidebar";
import TheNavbar from "@/components/navbar/TheNavbar.vue";
import { useBreadcrumb } from "@/composable/useBreadcrumb";
import { useGlobalSearch } from "@/composable/useGlobalSearch";
import StickyBreadcrumb from "@/components/utils/StickyBreadcrumb.vue";
import GlobalSearchModal from "@/components/utils/GlobalSearchModal.vue";
import GlobalActionsManager from "@/components/utils/GlobalActionsManager.vue";

export default {
  name: "app-layout",

  components: {
    TheNavbar,
    TheSidebar,
    StickyBreadcrumb,
    GlobalSearchModal,
    GlobalActionsManager
  },

  setup() {
    const store = useStore();
    const mobileBreakPointPX = 640;
    const tabletBreakPointPX = 768;

    const sidebarWidth = ref("16rem");
    const sidebarMinimizedWidth = ref(undefined);

    const isMobile = ref(false);
    const isTablet = ref(false);
    const isSidebarMinimized = computed(() => store.state.sidebar.isMinimized);
    const checkIsTablet = () => window.innerWidth <= tabletBreakPointPX;
    const checkIsMobile = () => window.innerWidth <= mobileBreakPointPX;

    const onResize = () => {
      store.dispatch("sidebar/setSidebarMinimized", checkIsTablet());

      isMobile.value = checkIsMobile();
      isTablet.value = checkIsTablet();
      sidebarMinimizedWidth.value = isMobile.value ? 0 : "4rem";
      sidebarWidth.value = isTablet.value ? "100%" : "16rem";
    };

    // Initialize global search
    const { setupKeyboardShortcuts } = useGlobalSearch();

    onMounted(() => {
      window.addEventListener("resize", onResize);
      // Setup global search keyboard shortcuts
      const cleanupKeyboardShortcuts = setupKeyboardShortcuts();

      // Store cleanup function for later use
      onBeforeUnmount(() => {
        window.removeEventListener("resize", onResize);
        cleanupKeyboardShortcuts();
      });
    });

    onBeforeRouteUpdate(() => {
      if (checkIsTablet()) {
        // Collapse sidebar after route change for Mobile
        store.dispatch("sidebar/setSidebarMinimized", true);
      }
    });

    onResize();

    const isFullScreenSidebar = computed(
      () => isTablet.value && !isSidebarMinimized.value
    );

    const onCloseSidebarButtonClick = () => {
      store.dispatch("sidebar/setSidebarMinimized", true);
    };

    // Initialize breadcrumb tracking
    useBreadcrumb();

    return {
      isSidebarMinimized,
      sidebarWidth,
      sidebarMinimizedWidth,
      isFullScreenSidebar,
      onCloseSidebarButtonClick
    };
  }
};
</script>

<style lang="scss">
$mobileBreakPointPX: 640px;
$tabletBreakPointPX: 768px;

.app-layout {
  height: 100vh;
  display: flex;
  flex-direction: column;

  &__navbar {
    min-height: 4rem;
  }

  &__content {
    display: flex;
    height: calc(100vh - 4rem);
    flex: 1;

    @media screen and (max-width: $tabletBreakPointPX) {
      height: calc(100vh - 6.5rem);
    }

    .app-layout__sidebar-wrapper {
      position: relative;
      height: 100%;
      background: var(--va-white);

      @media screen and (max-width: $tabletBreakPointPX) {
        &:not(.minimized) {
          width: 100%;
          height: 100%;
          position: fixed;
          top: 0;
          z-index: 999;
        }

        .va-sidebar:not(.va-sidebar--minimized) {
          // Z-index fix for preventing overflow for close button
          z-index: -1;

          .va-sidebar__menu {
            padding: 0;
          }
        }
      }
    }
  }

  &__page {
    flex-grow: 2;
    overflow-y: scroll;
    padding-bottom: 80px; // Space for sticky breadcrumb
  }
}
</style>
